import type { InputManager } from "./InputManager";

export interface ActionMapJson {
  bindings: Record<string, string[]>;
}

/**
 * Named actions → key bindings. Lets game code ask `actions.isDown("move-left")`
 * instead of hardcoding "KeyA". Players can later remap; bindings are
 * persistable as JSON.
 *
 * `axis()` consults a virtual axis named after the prefix shared by the
 * neg/pos actions when present (so `move-left`/`move-right` first reads the
 * virtual axis "move" if any virtual joystick / gamepad stick has set it).
 */
export class ActionMap {
  private bindings = new Map<string, string[]>();

  bind(action: string, ...codes: string[]): this {
    this.bindings.set(action, codes);
    return this;
  }

  /** Replace bindings for `action`. */
  rebind(action: string, codes: string[]): void {
    this.bindings.set(action, codes);
  }

  bindingsFor(action: string): string[] {
    return [...(this.bindings.get(action) ?? [])];
  }

  actions(): string[] {
    return [...this.bindings.keys()];
  }

  isDown(input: InputManager, action: string): boolean {
    const codes = this.bindings.get(action);
    if (!codes) return false;
    for (const c of codes) if (input.isDown(c)) return true;
    return false;
  }

  wasPressed(input: InputManager, action: string): boolean {
    const codes = this.bindings.get(action);
    if (!codes) return false;
    for (const c of codes) if (input.wasPressed(c)) return true;
    return false;
  }

  axis(input: InputManager, negAction: string, posAction: string): number {
    const prefix = sharedPrefix(negAction, posAction);
    if (prefix) {
      const v = input.getVirtualAxis(prefix);
      if (v) {
        const axisName = inferAxisName(negAction, posAction);
        if (axisName === "x") return v.x;
        if (axisName === "y") return v.y;
      }
    }
    return (this.isDown(input, posAction) ? 1 : 0) - (this.isDown(input, negAction) ? 1 : 0);
  }

  toJSON(): ActionMapJson {
    const out: Record<string, string[]> = {};
    for (const [k, v] of this.bindings) out[k] = [...v];
    return { bindings: out };
  }

  loadJSON(json: ActionMapJson): void {
    this.bindings.clear();
    for (const [k, v] of Object.entries(json.bindings)) this.bindings.set(k, [...v]);
  }
}

export const defaultActions = (): ActionMap =>
  new ActionMap()
    .bind("move-up", "KeyW", "ArrowUp")
    .bind("move-down", "KeyS", "ArrowDown")
    .bind("move-left", "KeyA", "ArrowLeft")
    .bind("move-right", "KeyD", "ArrowRight")
    .bind("interact", "KeyE", "Space")
    .bind("inventory", "KeyI", "Tab")
    .bind("chat", "Enter")
    .bind("debug", "Backquote");

/** "move-left" + "move-right" → "move". */
function sharedPrefix(a: string, b: string): string | undefined {
  const dashA = a.lastIndexOf("-");
  const dashB = b.lastIndexOf("-");
  if (dashA <= 0 || dashB <= 0) return undefined;
  const pa = a.slice(0, dashA);
  const pb = b.slice(0, dashB);
  return pa === pb ? pa : undefined;
}

/** "move-left"/"move-right" → "x"; "move-up"/"move-down" → "y". */
function inferAxisName(neg: string, pos: string): "x" | "y" | undefined {
  const sa = neg.split("-").pop();
  const sb = pos.split("-").pop();
  if (!sa || !sb) return undefined;
  const set = new Set([sa, sb]);
  if (set.has("left") || set.has("right")) return "x";
  if (set.has("up") || set.has("down")) return "y";
  return undefined;
}
